import { Auth0Provider, type AppState } from '@auth0/auth0-react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { loadAuth0Config } from './auth-config';

interface Auth0ProviderWithRouterProps {
  children: ReactNode;
}

/**
 * Wraps the SPA in a single Auth0Provider configured against the current tenant.
 *
 * The `onRedirectCallback` uses react-router's navigate() so users land on the
 * page they came from after the Auth0 round-trip.
 *
 * If Auth0 is not configured (no env vars), the children render unwrapped — the
 * SPA still works for the public landing page, but anything that calls
 * `useAuth0()` will see `isAuthenticated: false` permanently.
 */
export function AuthProviderWithRouter({ children }: Auth0ProviderWithRouterProps) {
  const navigate = useNavigate();
  const config = loadAuth0Config();

  if (!config) {
    return <>{children}</>;
  }

  const onRedirectCallback = (appState?: AppState) => {
    navigate(appState?.returnTo ?? window.location.pathname, { replace: true });
  };

  return (
    <Auth0Provider
      domain={config.domain}
      clientId={config.clientId}
      authorizationParams={{
        redirect_uri: window.location.origin,
        audience: config.audience,
        scope: config.scope,
      }}
      useRefreshTokens
      cacheLocation="memory"
      onRedirectCallback={onRedirectCallback}
    >
      {children}
    </Auth0Provider>
  );
}
